import Vue from 'vue'
import Vuex from 'vuex'
import createPersistedState from 'vuex-persistedstate'

// Day JS
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
dayjs.extend(localizedFormat)
// API
import { apiClient } from '@/services/'
// Page Modules
import * as dashboard from '@/views/dashboard/dashboardStore.js'
import * as preferences from '@/views/preferences/preferencesStore.js'
import * as evaluations from '@/views/evaluations/evalStore.js'
import * as etar from '@/views/etar/etarStore.js'
import * as distanceLearning from '@/views/distanceLearning/distanceLearningStore.js'
import * as schedule from '@/views/schedule/scheduleStore.js'

Vue.use(Vuex)

export default new Vuex.Store({
  modules: {
    preferences,
    dashboard,
    evaluations,
    etar,
    distanceLearning,
    schedule,
  },
  state: {
    // **** API URLS ****
    // If only one API is needed for an application, it should be defined in the services file. If more than one API is needed, define them in the store. All API URLs should reference the process.env variable defined in the `.env` and `.env.local` files. The variable is replaced by Octopus when the app is deployed. Below are examples for defining multiple APIs. These variables should be referenced in the URL for the API call.
    // apiCommon: process.env.VUE_APP_apiCommon,
    // apiFeature: process.env.VUE_APP_apiFeature,

    // App data
    appTitle: 'Student Portal',
    appSubtitle: 'Job Corps Portal for Student Excellence',
    appEnvironment: process.env.NODE_ENV,
    // Loading
    loadingApp: false,
    // Error
    responseMessage: '',
    responseType: '',
    msgTimeout: 5000,
    networkError: null,
    timeoutError: false,
    // Auth
    entraAuth: null,
    isAuthenticated: false,
    // User info
    adName: '',
    displayName: '',
    accessToken: null,
    entraUser: null,
    userInfo: null,
    // Date for schedules
    thisMonday: dayjs().startOf('week').add(1, 'days').format('L'),
  },
  mutations: {
    SET_LOADING_APP(state, status) {
      state.loadingApp = status
    },
    // Error
    SET_RESPONSE_MSG(state, status) {
      state.responseMessage = status
    },
    SET_RESPONSE_TYPE(state, status) {
      state.responseType = status
    },
    SET_NETWORK_ERROR(state, status) {
      state.networkError = status
    },
    SET_TIMEOUT_ERROR(state, status) {
      state.timeoutError = status
      // Clear error after 30 min
      setTimeout(() => {
        state.timeoutError = false
      }, 30 * 60 * 1000)
    },
    // Auth
    SET_ENTRA_AUTH(state, status) {
      state.entraAuth = status
    },
    // User info
    SET_ENTRA_USER(state, status) {
      state.entraUser = status
    },
    SET_ACCESS_TOKEN(state, status) {
      state.accessToken = status
    },
    SET_AD_NAME(state, status) {
      state.adName = status
    },
    SET_STUDENT_INFO(state, status) {
      state.userInfo = status
    },
    SET_USER_AUTH(state, status) {
      state.isAuthenticated = status
    },
    CLEAR_USER_DATA(state) {
      state.userInfo = {}
      state.isAuthenticated = false
    },
    CLEAR_RESPONSE(state) {
      state.responseType = ''
      state.responseMessage = ''
    },
  },
  actions: {
    // Only used in the dev and testing environments
    tempTestAdUser({ commit, dispatch, state }, adName) {
      const { entraUser } = state
      commit('RESET_STATE')
      commit('SET_ENTRA_USER', entraUser)
      commit('SET_LOADING_APP', true)
      commit('SET_AD_NAME', adName)
      dispatch('getUserInfo', adName)
    },
    // Production Code
    setUserInfo({ commit, dispatch, state }, user) {
      // store user
      apiClient.defaults.headers.common.Authorization = `Bearer ${state.accessToken}` // constructs auth string to API
      const adName = user.idTokenClaims.preferred_username.substr(
        0,
        user.idTokenClaims.preferred_username.indexOf('@') // constructs the AD username string
      )
      commit('SET_AD_NAME', adName) // sets AD username in store
      dispatch('getUserInfo', adName) // makes request to API
    },
    getUserInfo({ commit, dispatch }, adName) {
      // Login Tests
      return apiClient
        .get('/DoCdssLoginTests', {
          params: {
            sAdName: adName,
          },
        })
        .then(() => {
          // Get User Info
          return apiClient
            .get('/AdStudent', {
              params: {
                sAdName: adName,
              },
            })
            .then((response) => {
              commit('SET_LOADING_APP', false)
              commit('SET_STUDENT_INFO', response.data)
              // home page
              dispatch('preferences/fetchDashboardPreferences')
              dispatch('dashboard/getAnnouncements')
              // items for students and alumni
              if (!response.data.userRole.includes(2)) {
                dispatch('schedule/getStudentSchedule')
                dispatch('distanceLearning/getDistanceLearning')
                dispatch('etar/getEtars')
                dispatch('evaluations/getEvaluationsData')
              }
            })
        })
    },
  },
  getters: {
    displayName: (state) => {
      if (state.entraUser) {
        const fullName = state.entraUser.name.substring(
            0,
            state.entraUser.name.indexOf(' - ')
          ),
          firstName = fullName.split(fullName.indexOf(', '))
        return firstName[0]
      }
    },
    reportName: (state) => {
      return state.entraUser.name.substring(
        0,
        state.entraUser.name.indexOf(' - ')
      )
    },
    studId: (state) => {
      return state.userInfo.studId
    },
    userDisplayRoles: (state) => {
      if (state.userInfo.userRole.includes(2)) return 'Staff'
      else if (state.userInfo.userRole.includes(3)) return 'Alumni'
      return 'Student'
    },
    userInfo: (state, getters) => {
      return {
        userDisplayName: getters.displayName,
        userDisplayRoles: getters.userDisplayRoles,
        prevLastLoginDt: state.userInfo.adLastLoginDt,
        userRoles: state.userInfo.userRole,
      }
    },
    accessToken: (state) => {
      return state.accessToken
    },
    loggedIn: (state) => {
      return Boolean(state.entraUser)
    },
    alumni: (state) => {
      return state.userInfo.userRole == 3
    },
    getTheConfig: (state) => {
      return state.theConfig
    },
  },
  plugins: [
    createPersistedState({
      paths: ['userInfo.userRole'],
    }),
  ],
})
